import {
  COMPLIANCE_TYPES,
  type ComplianceDocType,
  type ComplianceDocument,
  getSupabase,
} from "@/lib/supabase";
import { useEffect, useState } from "react";

export type LatestComplianceByType = Record<ComplianceDocType, ComplianceDocument | null>;

function emptyMap(): LatestComplianceByType {
  return COMPLIANCE_TYPES.reduce((acc, t) => {
    acc[t] = null;
    return acc;
  }, {} as LatestComplianceByType);
}

/**
 * Fetch the latest compliance document of each type for one property.
 * RLS gates by tenancy; this hook just sorts and picks the most recent.
 */
export function useComplianceDocs(propertyId: string | null | undefined) {
  const [data, setData] = useState<LatestComplianceByType>(emptyMap());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!propertyId) {
      setData(emptyMap());
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      const supabase = getSupabase();
      const { data: rows, error: err } = (await supabase
        .from("compliance_documents")
        .select("*")
        .eq("property_id", propertyId)
        .order("issue_date", { ascending: false })) as {
        data: ComplianceDocument[] | null;
        error: { message: string } | null;
      };
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setLoading(false);
        return;
      }
      const next = emptyMap();
      for (const t of COMPLIANCE_TYPES) {
        next[t] = (rows ?? []).find((r) => r.type === t) ?? null;
      }
      setData(next);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  return { data, loading, error };
}
